import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getMe } from "./lib/api";
import { useEffect } from "react";
import { useSession } from "./lib/session";

import TopBar from "./components/TopBar";
import Footer from "./components/Footer";

// V5 pages — Honey/BMAC (current default).
import LandingV5 from "./pages/LandingV5";
import LoginV5 from "./pages/LoginV5";
import DashboardV5 from "./pages/DashboardV5";
import ProfileV5 from "./pages/ProfileV5";
import WallEmbed from "./pages/WallEmbed";
// V4 pages — Paper (reachable at /paper/* for compare).
import LandingV4 from "./pages/LandingV4";
import LoginV4 from "./pages/LoginV4";
import DashboardV4 from "./pages/DashboardV4";
import ProfileV4 from "./pages/ProfileV4";
// V3 pages — Glow (reachable at /glow/* for compare).
import LandingV3 from "./pages/LandingV3";
import LoginV3 from "./pages/LoginV3";
import DashboardV3 from "./pages/DashboardV3";
import ProfileV3 from "./pages/ProfileV3";
// V2 pages — Atelier (kept reachable at /atelier/* for comparison).
import LandingV2 from "./pages/LandingV2";
import LoginV2 from "./pages/LoginV2";
import DashboardV2 from "./pages/DashboardV2";
import ProfileV2 from "./pages/ProfileV2";
import SignWallFlow from "./pages/SignWallFlow";
import SubsWallPreview from "./pages/SubsWallPreview";

// Legacy pages — kept reachable at /legacy/* so we don't lose the
// donation/overlay flows while the subscription model is built out.
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Coins from "./pages/Coins";
import Onboarding from "./pages/Onboarding";
import DashboardLayout from "./pages/dashboard/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import DashboardSetup from "./pages/dashboard/DashboardSetup";
import DashboardOverlay from "./pages/dashboard/DashboardOverlay";
import DashboardDonations from "./pages/dashboard/DashboardDonations";
import DashboardFactions from "./pages/dashboard/DashboardFactions";
import DashboardAnalytics from "./pages/dashboard/DashboardAnalytics";
import DashboardSettings from "./pages/dashboard/DashboardSettings";
import Profile from "./pages/Profile";
import DonorFlow from "./pages/DonorFlow";
import Overlay from "./pages/Overlay";
import FakeCheckout from "./pages/FakeCheckout";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Refunds from "./pages/Refunds";
import AcceptableUse from "./pages/AcceptableUse";
import Contact from "./pages/Contact";
import Supporter from "./pages/Supporter";
import Dmca from "./pages/Dmca";

export default function App(): JSX.Element {
  // Hydrate the zustand session store from /api/me on app boot.
  const me = useQuery({ queryKey: ["me"], queryFn: getMe, staleTime: 60_000 });
  const setUser = useSession((s) => s.setUser);
  useEffect(() => {
    if (me.data) setUser(me.data.user);
  }, [me.data, setUser]);

  return (
    <Routes>
      {/* Standalone routes (no top bar) */}
      <Route path="/overlay/:streamKey" element={<Overlay />} />
      <Route path="/dev/fake-checkout" element={<FakeCheckout />} />

      {/* ─── V5 (Honey / BMAC) — current default ─── */}
      <Route path="/" element={<LandingV5 />} />
      <Route path="/login" element={<LoginV5 />} />
      <Route path="/dashboard" element={<DashboardV5 />} />
      <Route path="/wall-preview" element={<SubsWallPreview />} />
      <Route path=":handle/sign" element={<SignWallFlow />} />
      <Route path=":handle/embed" element={<WallEmbed />} />
      <Route path="/demo" element={<Navigate to="/demo-creator" replace />} />

      {/* ─── V4 (Paper) — reachable for compare ─── */}
      <Route path="/paper" element={<LandingV4 />} />
      <Route path="/paper/login" element={<LoginV4 />} />
      <Route path="/paper/dashboard" element={<DashboardV4 />} />
      <Route path="/paper/:handle" element={<ProfileV4 />} />

      {/* ─── V3 (Glow) — reachable for compare ─── */}
      <Route path="/glow" element={<LandingV3 />} />
      <Route path="/glow/login" element={<LoginV3 />} />
      <Route path="/glow/dashboard" element={<DashboardV3 />} />
      <Route path="/glow/:handle" element={<ProfileV3 />} />

      {/* ─── V2 (Atelier) — reachable for compare ─── */}
      <Route path="/atelier" element={<LandingV2 />} />
      <Route path="/atelier/login" element={<LoginV2 />} />
      <Route path="/atelier/dashboard" element={<DashboardV2 />} />
      <Route path="/atelier/:handle" element={<ProfileV2 />} />

      <Route path=":handle" element={<ProfileV5 />} />

      {/* Onboarding — V5 styled (was wrapped in legacy Chrome before) */}
      <Route path="/onboarding" element={<Onboarding />} />

      {/* ─── Legacy (TotemWall) — kept reachable for in-flight code ─── */}
      <Route element={<Chrome />}>
        <Route path="/legacy" element={<Landing />} />
        <Route path="/legacy/login" element={<Login />} />
        <Route path="/auth/reset" element={<ResetPassword />} />
        <Route path="/coins" element={<Coins />} />

        <Route path="/legacy/dashboard" element={<DashboardLayout />}>
          <Route index element={<DashboardHome />} />
          <Route path="setup" element={<DashboardSetup />} />
          <Route path="overlay" element={<DashboardOverlay />} />
          <Route path="donations" element={<DashboardDonations />} />
          <Route path="factions" element={<DashboardFactions />} />
          <Route path="analytics" element={<DashboardAnalytics />} />
          <Route path="settings" element={<DashboardSettings />} />
        </Route>

        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/refunds" element={<Refunds />} />
        <Route path="/acceptable-use" element={<AcceptableUse />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/dmca" element={<Dmca />} />

        <Route path="/s/:slug" element={<Supporter />} />
        <Route path="/legacy/:handle" element={<Profile />} />
        <Route path=":handle/donate" element={<DonorFlow />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function Chrome(): JSX.Element {
  return (
    <div className="app">
      <TopBar />
      <main className="app-main">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
